import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import Topbar from '../../components/Topbar/Topbar';
import RightSidebar from '../../components/RightSidebar/RightSidebar';
import './Messages.css';

import API_URL from '../../config';


const Icons = {
  search: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  ),
  send: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  ),
  plus: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  x: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  chevronLeft: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"/>
    </svg>
  ),
  menu: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  ),
};

const Messages = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [theme] = useState(() => localStorage.getItem('projabze_theme') || 'dark');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [conversations, setConversations] = useState([]);
  const [selectedConvoId, setSelectedConvoId] = useState(null);
  const [convoSearch, setConvoSearch] = useState('');
  const [loadingConvos, setLoadingConvos] = useState(true);

  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  const [showNewModal, setShowNewModal] = useState(false);
  const [userSuggestions, setUserSuggestions] = useState([]);

  const [showChatOnMobile, setShowChatOnMobile] = useState(false);

  const token = localStorage.getItem('projabze_token');

  useEffect(() => {
    const stored = localStorage.getItem('projabze_user');
    if (stored) {
      setUser(JSON.parse(stored));
    } else {
      navigate('/');
    }
  }, [navigate]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch { /* */ }
    finally { setLoadingConvos(false); }
  }, [token]);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  // Fetch messages
  const fetchMessages = useCallback(async (convoId) => {
    if (!token || !convoId) return;
    setLoadingMessages(true);
    try {
      const res = await fetch(`${API_URL}/api/messages/conversations/${convoId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch { /* */ }
    finally { setLoadingMessages(false); }
  }, [token]);

  // Mark as read
  const markAsRead = useCallback(async (convoId) => {
    if (!token || !convoId) return;
    try {
      await fetch(`${API_URL}/api/messages/conversations/${convoId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      setConversations(prev =>
        prev.map(c => c.id === convoId ? { ...c, unreadCount: 0 } : c)
      );
    } catch { /* */ }
  }, [token]);

  useEffect(() => {
    if (selectedConvoId) {
      fetchMessages(selectedConvoId);
      markAsRead(selectedConvoId);
      const interval = setInterval(() => fetchMessages(selectedConvoId), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedConvoId, fetchMessages, markAsRead]);

  // Auto scroll
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Send message
  const handleSend = async () => {
    const body = messageInput.trim();
    if (!body || !selectedConvoId || sendingMessage) return;

    setSendingMessage(true);
    setMessageInput('');

    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      body,
      senderId: user.id,
      sender: { id: user.id, firstName: user.firstName, lastName: user.lastName, avatar: user.avatar || '' },
      createdAt: new Date().toISOString(),
      _optimistic: true,
    };
    setMessages(prev => [...prev, optimisticMsg]);

    try {
      const res = await fetch(`${API_URL}/api/messages/conversations/${selectedConvoId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ body }),
      });
      if (res.ok) {
        const result = await res.json();
        setMessages(prev => prev.map(m => m.id === optimisticMsg.id ? result.data : m));
        fetchConversations();
      } else {
        setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id));
        setMessageInput(body);
      }
    } catch {
      setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id));
      setMessageInput(body);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Fetch user suggestions for new conversation
  const fetchSuggestions = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/social/users/suggestions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUserSuggestions(data.suggestions || []);
      }
    } catch { /* */ }
  };

  const openNewModal = () => {
    setShowNewModal(true);
    fetchSuggestions();
  };

  // Start new conversation
  const startConversation = async (recipientId) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/messages/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ recipientId }),
      });
      if (res.ok) {
        const data = await res.json();
        setShowNewModal(false);
        setUserSuggestions([]);
        await fetchConversations();
        setSelectedConvoId(data.conversationId);
        setShowChatOnMobile(true);
      }
    } catch { /* */ }
  };

  // Helpers
  const getInitials = (u) => {
    if (!u) return '?';
    return ((u.firstName?.[0] || '') + (u.lastName?.[0] || '')).toUpperCase() || '?';
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Simdi';
    if (mins < 60) return `${mins}dk`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}sa`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}g`;
    return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  const formatMessageTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  const truncate = (str, len = 40) => {
    if (!str) return '';
    return str.length > len ? str.slice(0, len) + '...' : str;
  };

  const handleLogout = () => {
    localStorage.removeItem('projabze_token');
    localStorage.removeItem('projabze_user');
    navigate('/');
  };

  const handleSelectConvo = (convoId) => {
    setSelectedConvoId(convoId);
    setShowChatOnMobile(true);
  };

  // Filter conversations
  const filteredConvos = conversations.filter(c => {
    if (!convoSearch.trim()) return true;
    const name = `${c.otherUser?.firstName || ''} ${c.otherUser?.lastName || ''}`.toLowerCase();
    return name.includes(convoSearch.toLowerCase());
  });

  const selectedConvo = conversations.find(c => c.id === selectedConvoId);
  const otherUser = selectedConvo?.otherUser || null;

  if (!user) return null;

  const initials = getInitials(user);

  return (
    <div className={`msg-page ${theme}`}>
      <Topbar user={user} onLogout={handleLogout} />
      {/* SIDEBAR */}
      <Sidebar activeId="messages" user={user} onLogout={handleLogout} className={sidebarOpen ? 'open' : ''} />

      {/* MESSAGE CONTENT (conversation list + chat) */}
      <div className="msg-content">
        {/* CONVERSATION LIST */}
        <div className={`msg-convo-list ${showChatOnMobile ? 'hide-mobile' : ''}`}>
          <div className="msg-convo-header">
            <button className="msg-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {Icons.menu}
            </button>
            <h2>Mesajlar</h2>
            <button className="msg-new-btn" onClick={openNewModal} title="Yeni Mesaj">
              {Icons.plus}
            </button>
          </div>
          <div className="msg-convo-search">
            <span className="msg-convo-search-icon">{Icons.search}</span>
            <input
              type="text"
              placeholder="Konusmalarda ara..."
              value={convoSearch}
              onChange={e => setConvoSearch(e.target.value)}
            />
          </div>
          <div className="msg-convo-items">
            {loadingConvos ? (
              <div className="msg-convo-loading">
                <div className="msg-spinner"></div>
                <span>Yükleniyor...</span>
              </div>
            ) : filteredConvos.length === 0 ? (
              <div className="msg-convo-empty">
                {convoSearch ? 'Sonuc bulunamadi' : 'Henuz konusma yok'}
              </div>
            ) : (
              filteredConvos.map(convo => {
                const other = convo.otherUser;
                const isSelected = convo.id === selectedConvoId;
                const otherAvatarSrc = other?.avatar ? `${API_URL}${other.avatar}` : null;
                return (
                  <div
                    key={convo.id}
                    className={`msg-convo-item ${isSelected ? 'selected' : ''} ${convo.unreadCount > 0 ? 'unread' : ''}`}
                    onClick={() => handleSelectConvo(convo.id)}
                  >
                    <div className="msg-convo-avatar">
                      {otherAvatarSrc ? <img src={otherAvatarSrc} alt="" /> : getInitials(other)}
                    </div>
                    <div className="msg-convo-info">
                      <div className="msg-convo-row">
                        <span className="msg-convo-name">{other?.firstName} {other?.lastName}</span>
                        <span className="msg-convo-time">{formatTime(convo.lastMessage?.createdAt || convo.createdAt)}</span>
                      </div>
                      <div className="msg-convo-row">
                        <span className="msg-convo-preview">{truncate(convo.lastMessage?.body)}</span>
                        {convo.unreadCount > 0 && (
                          <span className="msg-convo-badge">{convo.unreadCount > 99 ? '99+' : convo.unreadCount}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* CHAT PANEL */}
        <div className={`msg-chat ${showChatOnMobile ? 'show-mobile' : ''}`}>
          {selectedConvo && otherUser ? (
            <>
              <div className="msg-chat-header">
                <button className="msg-back-btn" onClick={() => setShowChatOnMobile(false)}>
                  {Icons.chevronLeft}
                </button>
                <div className="msg-chat-header-avatar">
                  {otherUser.avatar ? <img src={`${API_URL}${otherUser.avatar}`} alt="" /> : getInitials(otherUser)}
                </div>
                <div className="msg-chat-header-info">
                  <span className="msg-chat-header-name">{otherUser.firstName} {otherUser.lastName}</span>
                </div>
              </div>

              <div className="msg-chat-messages">
                {loadingMessages ? (
                  <div className="msg-chat-loading"><div className="msg-spinner"></div></div>
                ) : messages.length === 0 ? (
                  <div className="msg-chat-start">
                    <div className="msg-chat-start-avatar">
                      {otherUser.avatar ? <img src={`${API_URL}${otherUser.avatar}`} alt="" /> : getInitials(otherUser)}
                    </div>
                    <h3>{otherUser.firstName} {otherUser.lastName}</h3>
                    <p>Konusmaya baslamak icin bir mesaj gonderin.</p>
                  </div>
                ) : (
                  <>
                    {messages.map((msg, idx) => {
                      const isMine = (msg.senderId || msg.sender?.id) === user.id;
                      const prevMsg = messages[idx - 1];
                      const prevIsMine = prevMsg ? (prevMsg.senderId || prevMsg.sender?.id) === user.id : null;
                      const isGrouped = prevIsMine === isMine;

                      return (
                        <div
                          key={msg.id}
                          className={`msg-bubble-wrapper ${isMine ? 'mine' : 'theirs'} ${isGrouped ? 'grouped' : ''}`}
                        >
                          <div className={`msg-bubble ${isMine ? 'mine' : 'theirs'} ${msg._optimistic ? 'optimistic' : ''}`}>
                            <p>{msg.body}</p>
                            <span className="msg-bubble-time">{formatMessageTime(msg.createdAt)}</span>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              <div className="msg-chat-input-bar">
                <textarea
                  className="msg-chat-input"
                  placeholder="Mesajinizi yazin..."
                  value={messageInput}
                  onChange={e => {
                    setMessageInput(e.target.value);
                    // Auto-grow: scrollHeight'a göre yüksekliği ayarla
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 180) + 'px';
                  }}
                  onKeyDown={handleKeyDown}
                  disabled={sendingMessage}
                  rows={1}
                />
                <button
                  className="msg-send-btn"
                  onClick={handleSend}
                  disabled={!messageInput.trim() || sendingMessage}
                  title="Gonder (Shift+Enter ile yeni satir)"
                >
                  {Icons.send}
                </button>
              </div>
            </>
          ) : (
            <div className="msg-chat-empty">
              <div className="msg-chat-empty-icon">{Icons.messages}</div>
              <h3>Mesajlar</h3>
              <p>Bir konuşma seçin veya yeni mesaj başlatın</p>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT SIDEBAR */}
      <RightSidebar />

      {/* NEW CONVERSATION MODAL */}
      {showNewModal && (
        <div className="msg-modal-overlay" onClick={(e) => { if (e.target.classList.contains('msg-modal-overlay')) setShowNewModal(false); }}>
          <div className="msg-modal">
            <div className="msg-modal-header">
              <h3>Yeni Mesaj</h3>
              <button className="msg-modal-close" onClick={() => { setShowNewModal(false); setUserSuggestions([]); }}>
                {Icons.x}
              </button>
            </div>
            <div className="msg-modal-body">
              <p className="msg-modal-desc">Mesaj göndermek istediğiniz kullanıcıyı seçin</p>
              <div className="msg-modal-results">
                {userSuggestions.length === 0 ? (
                  <div className="msg-modal-empty">Kullanıcı bulunamadı</div>
                ) : (
                  userSuggestions.map(u => {
                    const uAvatar = u.avatar ? `${API_URL}${u.avatar}` : null;
                    return (
                      <div
                        key={u.id}
                        className="msg-modal-user"
                        onClick={() => startConversation(u.id)}
                      >
                        <div className="msg-modal-user-avatar">
                          {uAvatar ? <img src={uAvatar} alt="" /> : getInitials(u)}
                        </div>
                        <div className="msg-modal-user-info">
                          <span className="msg-modal-user-name">{u.firstName} {u.lastName}</span>
                          {u.city && <span className="msg-modal-user-email">{u.city}</span>}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {sidebarOpen && <div className="msg-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
    </div>
  );
};

export default Messages;
